import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Check, Loader2, MessageSquare, Minus, Plus, RefreshCw, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';

const BLUE = '#0A84FF';
const tz = () => ({ headers: { 'X-TZ-Offset': String(new Date().getTimezoneOffset()) } });
const PILLARS = {
  hydrate: { title: 'Hydrate', img: '/recovery-hydrate.jpg?v=3' },
  refuel: { title: 'Refuel', img: '/recovery-refuel.jpg?v=3' },
  sleep: { title: 'Sleep', img: '/recovery-sleep.jpg?v=3' },
};

const _half = (v) => Math.floor(v + 0.5);
const litres = (ml) => `${((ml || 0) / 1000).toFixed(1)}L`;
const hours = (h) => (h ? `${Math.floor(h)}h ${Math.round((h % 1) * 60)}m` : '—');

/** Count-up number, respects reduced motion. */
const Animated = ({ value, format = (v) => v, testId }) => {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { setShown(value); return; }
    const start = performance.now(); const a = from.current; const b = value;
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / 450);
      setShown(a + (b - a) * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(tick); else from.current = b;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span data-testid={testId}>{format(shown)}</span>;
};

/** 7-day bars with a dashed target line. Auto-filled days render faded. */
const WeekBars = ({ week, valueKey, target, color = BLUE, testId }) => {
  const max = Math.max(target || 0, ...week.map((d) => d[valueKey] || 0), 1);
  const sourceKey = valueKey === 'water_ml' ? 'water_source' : 'sleep_source';
  return (
    <div className="mt-3" data-testid={testId}>
      <div className="relative h-20 flex items-end gap-1.5">
        {target > 0 && (
          <div className="absolute left-0 right-0 border-t border-dashed border-white/25"
               style={{ bottom: `${(target / max) * 100}%` }} />
        )}
        {week.map((d) => {
          const v = d[valueKey] || 0;
          const hit = target > 0 && v >= target;
          return (
            <div key={d.date} className="flex-1 rounded-t-md transition-all"
                 data-testid={`${testId}-bar-${d.date}${d[sourceKey] === 'auto' ? '-auto' : ''}`}
                 style={{
                   height: `${Math.max((v / max) * 100, v ? 4 : 2)}%`,
                   background: v ? color : 'rgba(255,255,255,0.10)',
                   boxShadow: hit ? `0 0 10px ${color}` : 'none',
                   opacity: d[sourceKey] === 'auto' ? 0.5 : 1,
                 }} />
          );
        })}
      </div>
      <div className="mt-1 flex gap-1.5 text-[10px] text-white/35">
        {week.map((d) => (
          <span key={d.date} className="flex-1 text-center">
            {new Date(`${d.date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'narrow' })}
          </span>
        ))}
      </div>
    </div>
  );
};

const MacroRow = ({ label, value, target, testId }) => (
  <div className="flex-1 min-w-0" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-[0.12em] text-white/40">{label}</p>
    <p className="font-heading font-black text-base text-white leading-tight">
      <Animated value={value} format={(v) => Math.round(v)} />
      <span className="text-white/35 text-xs font-normal">{target ? ` / ${target}` : ''}</span>
    </p>
    <div className="mt-1 h-1 rounded-full bg-white/10 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, target ? (value / target) * 100 : 0)}%`, background: BLUE }} />
    </div>
  </div>
);

/** Slide-up logger for one recovery pillar. */
export const RecoverySheet = ({ pillar, date, today, open, onOpenChange, data, onChanged }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [custom, setCustom] = useState(300);
  const [desc, setDesc] = useState('');
  const [estimate, setEstimate] = useState(null);
  const [baseName, setBaseName] = useState('');
  const [portion, setPortion] = useState(1);
  const [sleepHours, setSleepHours] = useState(data?.totals?.sleep_hours || data?.sleep_settings?.usual_hours || 8);
  const [bedtime, setBedtime] = useState(data?.sleep_settings?.bedtime || '23:00');
  const [wakeTime, setWakeTime] = useState(data?.sleep_settings?.wake_time || '07:00');
  const fileRef = useRef(null);

  useEffect(() => {
    setSleepHours(data?.totals?.sleep_hours || data?.sleep_settings?.usual_hours || 8);
    if (data?.sleep_settings?.bedtime) setBedtime(data.sleep_settings.bedtime);
    if (data?.sleep_settings?.wake_time) setWakeTime(data.sleep_settings.wake_time);
  }, [data]);

  if (!pillar || !data) return null;
  const meta = PILLARS[pillar];
  const t = data.totals || {};
  const target = data.targets || {};
  const week = data.week || [];
  const logDate = date || data.date;
  const isToday = !today || logDate === today;
  const dayLabel = isToday
    ? 'Today'
    : new Date(`${logDate}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' });

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await onChanged?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'That did not save. Try again.'); }
    finally { setBusy(false); }
  };

  const logWater = (ml) => run(async () => {
    await api.post('/recovery/water', { ml, date: logDate }, tz());
    toast.success(`+${ml}ml`);
  });

  // Shrink to 1024px on the long edge and re-encode as JPEG — a phone photo is
  // several MB and the model reads it no better than this.
  const shrinkPhoto = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, 1024 / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });

  const analysePhoto = async (file) => {
    setBusy(true);
    try {
      const image_base64 = await shrinkPhoto(file);
      const res = await api.post('/recovery/meal/analyze', { image_base64 }, tz());
      setEstimate(res.data.estimate); setBaseName(res.data.estimate.name); setPortion(1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't read that photo. Type what you ate instead.");
    } finally { setBusy(false); }
  };

  const analyseText = () => run(async () => {
    const res = await api.post('/recovery/meal/analyze', { description: desc }, tz());
    setEstimate(res.data.estimate); setBaseName(res.data.estimate.name); setPortion(1);
  });

  // The user corrected what the photo actually was - re-score from their words.
  const reEstimate = () => run(async () => {
    const res = await api.post('/recovery/meal/analyze', { description: estimate.name }, tz());
    setEstimate({ ...res.data.estimate, name: estimate.name });
    setBaseName(estimate.name);
    setPortion(1);
    toast.success('Updated from your description');
  });

  const saveMeal = () => run(async () => {
    await api.post('/recovery/meal', { ...estimate, portion, date: logDate }, tz());
    setEstimate(null); setBaseName(''); setDesc('');
    toast.success('Meal logged');
  });

  // Re-log a meal they eat often, exactly as it was last logged.
  const logFavourite = (fav) => run(async () => {
    await api.post('/recovery/meal', {
      name: fav.name, calories: fav.calories, protein_g: fav.protein_g,
      carbs_g: fav.carbs_g, fat_g: fav.fat_g, source: fav.source, date: logDate,
    }, tz());
    toast.success(`${fav.name} logged`);
  });

  const saveSleep = () => run(async () => {
    await api.post('/recovery/sleep', { hours: sleepHours, date: logDate }, tz());
    toast.success('Sleep confirmed');
  });

  const saveSchedule = () => run(async () => {
    await api.put('/recovery/sleep-schedule', { bedtime, wake_time: wakeTime });
    toast.success('Usual nights saved — this logs itself from now on');
  });

  const toggleAutoWater = (enabled) => run(async () => {
    await api.put('/recovery/auto-water', { enabled });
    toast.success(enabled ? 'Empty days will show your target' : 'Auto-fill off');
  });

  // The coach already sees today's gap and their profile, so just open the question.
  const proteinLeft = Math.max((target.protein_g || 0) - (t.protein_g || 0), 0);
  const askCoachWhatToEat = () => {
    const q = proteinLeft > 0
      ? `What should I eat next to hit my protein and calories today?`
      : `What should I eat next today?`;
    onOpenChange?.(false);
    navigate(`/chat?ask=${encodeURIComponent(q)}`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom"
        className="bg-[#07090C] border-t border-white/10 rounded-t-3xl max-h-[88vh] overflow-y-auto pb-[calc(6.5rem+env(safe-area-inset-bottom,0px))]"
        data-testid={`recovery-sheet-${pillar}`}>
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading font-black uppercase tracking-tight text-white flex items-center gap-3">
            <img src={meta.img} alt="" className="w-9 h-9 rounded-lg object-cover" />
            <span className="min-w-0">
              {meta.title}
              <span className="block text-[11px] font-normal normal-case tracking-normal text-white/45"
                    data-testid="recovery-sheet-day">{dayLabel}</span>
            </span>
          </SheetTitle>
        </SheetHeader>

        {pillar === 'hydrate' && (
          <div className="pb-8">
            <p className="mt-3 font-heading font-black text-3xl text-white">
              <Animated value={t.water_ml || 0} format={litres} testId="water-total" />
              <span className="text-base text-white/35 font-normal"> of {litres(target.water_ml)}</span>
            </p>
            <p className="text-xs text-white/40" data-testid="water-basis">{target.water_basis}</p>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {[['Glass', 250], ['Bottle', 500], ['Large', 750]].map(([label, ml]) => (
                <button key={ml} disabled={busy} onClick={() => logWater(ml)}
                  className="py-3 rounded-xl border border-white/10 bg-white/[0.04] active:scale-95 transition-transform disabled:opacity-50"
                  data-testid={`water-add-${ml}`}>
                  <span className="block font-heading font-bold text-sm text-white">{label}</span>
                  <span className="block text-[11px] text-white/40">{ml}ml</span>
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button onClick={() => setCustom((c) => Math.max(50, c - 50))} className="p-2.5 rounded-lg bg-white/[0.06]" data-testid="water-custom-minus"><Minus className="w-4 h-4 text-white" /></button>
              <span className="flex-1 text-center text-sm text-white/70" data-testid="water-custom-value">{custom}ml</span>
              <button onClick={() => setCustom((c) => Math.min(2000, c + 50))} className="p-2.5 rounded-lg bg-white/[0.06]" data-testid="water-custom-plus"><Plus className="w-4 h-4 text-white" /></button>
              <button disabled={busy} onClick={() => logWater(custom)} className="px-4 py-2.5 rounded-lg font-heading font-bold text-sm text-white" style={{ background: BLUE }} data-testid="water-custom-add">Add</button>
            </div>
            <button disabled={busy} onClick={() => run(async () => { await api.delete(`/recovery/water/last?date=${logDate}`, tz()); toast.success('Removed'); })}
              className="mt-3 text-xs text-white/40 flex items-center gap-1.5" data-testid="water-undo">
              <Undo2 className="w-3.5 h-3.5" /> Undo last
            </button>
            <WeekBars week={week} valueKey="water_ml" target={target.water_ml} testId="water-week-graph" />
            <p className="mt-2 text-xs text-white/45" data-testid="water-advice">
              {(t.water_ml || 0) >= (target.water_ml || 0)
                ? "Target hit — nice work."
                : `${litres((target.water_ml || 0) - (t.water_ml || 0))} to go today.`}
            </p>

            {/* Auto-fill: same idea as the sleep schedule, display only */}
            <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.04] p-3 flex items-center gap-3"
                 data-testid="water-autofill-row">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] uppercase tracking-[0.12em] text-white/45">Fill in days I forget</p>
                <p className="mt-1 text-[11px] text-white/35">
                  {data.auto_water
                    ? `Past days with nothing logged show your ${litres(target.water_ml)} target, faded. A real log always replaces it.`
                    : 'Shows your daily target on past days you never logged — today is always left blank.'}
                </p>
              </div>
              <Switch checked={!!data.auto_water} disabled={busy} onCheckedChange={toggleAutoWater}
                      data-testid="water-autofill-toggle" />
            </div>
          </div>
        )}

        {pillar === 'refuel' && (
          <div className="pb-8">
            <div className="mt-3 flex gap-3">
              <MacroRow label="Calories" value={t.calories || 0} target={target.calories} testId="macro-calories" />
              <MacroRow label="Protein" value={t.protein_g || 0} target={target.protein_g} testId="macro-protein" />
            </div>
            <div className="mt-3 flex gap-3">
              <MacroRow label="Carbs" value={t.carbs_g || 0} target={target.carbs_g} testId="macro-carbs" />
              <MacroRow label="Fat" value={t.fat_g || 0} target={target.fat_g} testId="macro-fat" />
            </div>
            <p className="mt-2 text-xs text-white/40" data-testid="nutrition-basis">{target.basis}</p>

            {!estimate ? (
              <>
                {(data.favourites || []).length > 0 && (
                  <div className="mt-4" data-testid="meal-favourites">
                    <p className="text-[11px] uppercase tracking-[0.12em] text-white/40">Your usual meals — one tap</p>
                    <div className="mt-2 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                      {data.favourites.map((fav, i) => (
                        <button key={fav.name} disabled={busy} onClick={() => logFavourite(fav)}
                          className="shrink-0 max-w-[62%] text-left px-3 py-2 rounded-xl border border-white/10 bg-white/[0.04] active:scale-[0.97] transition-transform disabled:opacity-50"
                          data-testid={`meal-favourite-${i}`}>
                          <span className="block text-[12px] font-heading font-bold text-white truncate">{fav.name}</span>
                          <span className="block text-[11px] text-white/45">
                            {fav.calories} kcal · {fav.protein_g}p{fav.count > 1 ? ` · ${fav.count}×` : ''}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(e) => e.target.files?.[0] && analysePhoto(e.target.files[0])} data-testid="meal-photo-input" />
                <button disabled={busy} onClick={() => fileRef.current?.click()}
                  className="mt-3 w-full py-3 rounded-xl font-heading font-bold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ background: BLUE }} data-testid="meal-photo-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Photo of your meal
                </button>
                <div className="mt-3 flex gap-2">
                  <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="or type what you ate"
                    className="flex-1 px-3 py-2.5 rounded-xl bg-white/[0.05] border border-white/10 text-sm text-white placeholder:text-white/30"
                    data-testid="meal-text-input" />
                  <button disabled={busy || desc.trim().length < 3} onClick={analyseText}
                    className="px-4 rounded-xl border border-white/15 text-sm font-heading font-bold text-white disabled:opacity-40"
                    data-testid="meal-text-btn">Estimate</button>
                </div>
              </>
            ) : (
              <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid="meal-confirm-card">
                <input
                  value={estimate.name}
                  onChange={(e) => setEstimate({ ...estimate, name: e.target.value.slice(0, 80) })}
                  className="w-full bg-transparent font-heading font-bold text-white outline-none border-b border-dashed border-white/20 focus:border-white/50 pb-1"
                  aria-label="Meal name"
                  data-testid="meal-name-input" />
                <p className="text-sm text-white/60 mt-2" data-testid="meal-estimate-line">
                  {_half(estimate.calories * portion)} kcal · {_half(estimate.protein_g * portion)}p ·{' '}
                  {_half(estimate.carbs_g * portion)}c · {_half(estimate.fat_g * portion)}f
                </p>
                <p className="text-[11px] text-white/35 mt-1">
                  Estimate ({estimate.confidence} confidence) — fix the name or portion if it looks off
                </p>
                {estimate.name.trim() !== (baseName || '').trim() && estimate.name.trim().length > 2 && (
                  <button disabled={busy} onClick={reEstimate}
                    className="mt-3 w-full py-2.5 rounded-xl border text-sm font-heading font-bold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ borderColor: BLUE, color: BLUE }}
                    data-testid="meal-reestimate-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Re-estimate from "{estimate.name.trim().slice(0, 28)}"
                  </button>
                )}
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {[0.5, 1, 1.5, 2].map((p) => (
                    <button key={p} onClick={() => setPortion(p)}
                      className={`py-2 rounded-lg text-sm font-heading font-bold ${portion === p ? 'text-white' : 'text-white/50 bg-white/[0.05]'}`}
                      style={portion === p ? { background: BLUE } : undefined}
                      data-testid={`meal-portion-${String(p).replace('.', '-')}`}>{p}×</button>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <button disabled={busy} onClick={saveMeal} className="flex-1 py-3 rounded-xl font-heading font-bold text-sm text-white flex items-center justify-center gap-2" style={{ background: BLUE }} data-testid="meal-save-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Log it
                  </button>
                  <button onClick={() => { setEstimate(null); setBaseName(''); }} className="px-4 rounded-xl border border-white/15 text-sm text-white/70" data-testid="meal-cancel-btn">Cancel</button>
                </div>
              </div>
            )}
            <WeekBars week={week} valueKey="protein_g" target={target.protein_g} testId="protein-week-graph" />
            <p className="mt-2 text-xs text-white/45">Protein across the last 7 days, dashed line is your daily target.</p>
            <button onClick={askCoachWhatToEat}
              className="mt-3 w-full py-3 rounded-xl border border-white/15 bg-white/[0.04] font-heading font-bold text-sm text-white flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
              data-testid="meal-ask-coach-btn">
              <MessageSquare className="w-4 h-4" style={{ color: BLUE }} />
              Ask coach what to eat next
            </button>
            <p className="mt-1.5 text-[11px] text-white/35" data-testid="meal-ask-coach-hint">
              {proteinLeft > 0
                ? `${proteinLeft}g protein and ${Math.max((target.calories || 0) - (t.calories || 0), 0)} kcal left today`
                : 'Targets met for today — ask anyway for tomorrow'}
            </p>
          </div>
        )}

        {pillar === 'sleep' && (
          <div className="pb-8">
            <p className="mt-3 font-heading font-black text-3xl text-white">
              <Animated value={t.sleep_hours || 0} format={hours} testId="sleep-total" />
              <span className="text-base text-white/35 font-normal"> of {target.sleep_hours}h</span>
            </p>
            <p className="text-xs text-white/40" data-testid="sleep-source">
              {t.sleep_source === 'confirmed'
                ? 'Confirmed by you'
                : data.sleep_settings?.usual_hours
                  ? 'Logged automatically from your usual hours — tap to adjust'
                  : 'Set your usual hours in Settings and this logs itself'}
            </p>
            <div className="mt-4 flex items-center gap-3">
              <button onClick={() => setSleepHours((h) => Math.max(1, +(h - 0.25).toFixed(2)))} className="p-3 rounded-xl bg-white/[0.06]" data-testid="sleep-minus"><Minus className="w-4 h-4 text-white" /></button>
              <span className="flex-1 text-center font-heading font-black text-2xl text-white" data-testid="sleep-value">{hours(sleepHours)}</span>
              <button onClick={() => setSleepHours((h) => Math.min(16, +(h + 0.25).toFixed(2)))} className="p-3 rounded-xl bg-white/[0.06]" data-testid="sleep-plus"><Plus className="w-4 h-4 text-white" /></button>
            </div>
            <button disabled={busy} onClick={saveSleep}
              className="mt-3 w-full py-3 rounded-xl font-heading font-bold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: BLUE }} data-testid="sleep-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {isToday ? 'Confirm last night' : `Confirm ${dayLabel}`}
            </button>
            <WeekBars week={week} valueKey="sleep_hours" target={target.sleep_hours} testId="sleep-week-graph" />
            <p className="mt-2 text-xs text-white/45" data-testid="sleep-advice">
              {(() => {
                const short = week.slice(-2).filter((d) => d.sleep_hours && d.sleep_hours < 6).length;
                if (short >= 2) return 'Two short nights — recovery is where the gains land. Try 45 minutes earlier tonight.';
                if (!t.sleep_hours && !data.sleep_settings?.usual_hours) return 'Set your usual nights below and this fills itself in every night.';
                if ((t.sleep_hours || 0) && (t.sleep_hours || 0) < (target.sleep_hours || 8) - 1) return 'A little short of your target — an earlier night will help tomorrow.';
                return 'Faded bars were filled in automatically, solid bars you confirmed.';
              })()}
            </p>

            {/* Usual nights: set once, then every night logs itself */}
            <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.04] p-3" data-testid="sleep-schedule-editor">
              <p className="text-[11px] uppercase tracking-[0.12em] text-white/45">Your usual nights</p>
              <div className="mt-2 flex items-center gap-2">
                <label className="flex-1">
                  <span className="block text-[10px] text-white/35 mb-1">Bedtime</span>
                  <input type="time" value={bedtime} onChange={(e) => setBedtime(e.target.value)}
                    className="w-full px-2.5 py-2 rounded-lg bg-white/[0.06] border border-white/10 text-sm text-white"
                    data-testid="sleep-bedtime-input" />
                </label>
                <label className="flex-1">
                  <span className="block text-[10px] text-white/35 mb-1">Wake up</span>
                  <input type="time" value={wakeTime} onChange={(e) => setWakeTime(e.target.value)}
                    className="w-full px-2.5 py-2 rounded-lg bg-white/[0.06] border border-white/10 text-sm text-white"
                    data-testid="sleep-waketime-input" />
                </label>
                <button disabled={busy || !bedtime || !wakeTime} onClick={saveSchedule}
                  className="self-end px-4 py-2 rounded-lg font-heading font-bold text-sm text-white disabled:opacity-40"
                  style={{ background: BLUE }} data-testid="sleep-schedule-save">Save</button>
              </div>
              <p className="mt-2 text-[11px] text-white/35">
                {data.sleep_settings?.usual_hours
                  ? `Logging ${hours(data.sleep_settings.usual_hours)} automatically each night — confirm only when it was different.`
                  : 'Once saved, every night logs itself. You only tap when it was different.'}
              </p>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default RecoverySheet;
